import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, SPACING } from "@store/theme/common/design";
import { clearfixStyles } from "@store/theme/common/mixins";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: ProductDetailsViewStyles;
}

export interface ProductDetailsViewStyles {
    container?: InjectableCss;
    topContainer?: GridContainerProps;
    mobileDetailsGridItem?: GridItemProps;
    mobileDetailsHidden?: HiddenProps;
    leftColumnGridItem?: GridItemProps;
    imageWrapper?: InjectableCss;
    rightColumnGridItem?: GridItemProps;
    rightColumnInnerContainer?: InjectableCss;
    rightColumnInnerPricesContainer?: InjectableCss;
    bottomContainer?: GridContainerProps;
    bottomGridItem?: GridItemProps;
}

export const productDetailsViewStyles: ProductDetailsViewStyles = {
    container: {},
    topContainer: {
        gap: 40,
        css: css`
            ${clearfixStyles()}
            padding: 20px; /* fixed px to fit Grid gap */
            border: 1px solid ${COLORS.border.base};
            margin-bottom: 20px;
        `,
    },
    mobileDetailsGridItem: {
        width: [12, 12, 0, 0, 0],
        css: css`
            padding-bottom: 0;
        `,
    },
    mobileDetailsHidden: {
        css: css`
            width: 100%;
        `,
    },
    leftColumnGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            padding: 10px;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["10px 20px", null, "20px", "50px 35px 50px 50px", null], properties: ["padding"] }],
                    mediaOptions.min,
                )}
        `,
    },
    imageWrapper: {
        css: css`
            width: 100%;
        `,
    },
    rightColumnGridItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            padding: 10px;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["10px 20px", null, "20px", "50px 50px 50px 35px", null], properties: ["padding"] }],
                    mediaOptions.min,
                )}
        `,
    },
    rightColumnInnerContainer: {
        css: css`
            width: 100%;
        `,
    },
    rightColumnInnerPricesContainer: {
        css: css`
            > div {
                display: flex;
                flex-wrap: wrap;
                margin-bottom: ${SPACING.standard};
                ${({ theme }) =>
                    breakpointValuesMediaQueries(
                        theme,
                        [{ values: ["column", null, "row", null, null], properties: ["flex-direction"] }],
                        mediaOptions.min,
                    )}
            }

            section {
                display: inline-flex;
                width: auto;
                margin-right: ${SPACING.xlarge};
            }
        `,
    },
    bottomGridItem: { width: 12 },
};

const StoreProductDetailsView: FC<OwnProps> = ({ id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(productDetailsViewStyles, extendedStyles));
    return (
        <StyledWrapper {...styles.container}>
            <GridContainer {...styles.topContainer}>
                <GridItem {...styles.mobileDetailsGridItem}>
                    <Hidden above="sm" {...styles.mobileDetailsHidden}>
                        <Zone contentId={id} zoneName="Content0" />
                    </Hidden>
                </GridItem>
                <GridItem {...styles.leftColumnGridItem}>
                    <StyledWrapper {...styles.imageWrapper}>
                        <Zone contentId={id} zoneName="Content1" />
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.rightColumnGridItem}>
                    <StyledWrapper {...styles.rightColumnInnerContainer}>
                        <Hidden above="sm" {...styles.mobileDetailsHidden}>
                            <Zone contentId={id} zoneName="Content6" />
                        </Hidden>
                        <Hidden below="md">
                            <Zone contentId={id} zoneName="Content2" />
                        </Hidden>
                        <StyledWrapper {...styles.rightColumnInnerPricesContainer}>
                            <Zone contentId={id} zoneName="Content5" />
                        </StyledWrapper>
                        <Zone contentId={id} zoneName="Content3" />
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
            <GridContainer {...styles.bottomContainer}>
                <GridItem {...styles.bottomGridItem}>
                    <Zone contentId={id} zoneName="Content4" />
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const storeProductDetailsView: WidgetModule = {
    component: StoreProductDetailsView,
    definition: {
        displayName: "ViewStore",
        group: "Product Details",
        isSystem: true,
    },
};

export default storeProductDetailsView;
